import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Add, AutoAwesomeOutlined, SearchOutlined } from "@mui/icons-material";
import Header from "../../components/Header";
import EntryDialog from "./EntryDialog";
import PromptDialog from "./PromptDialog";
import { useGetEntriesQuery, useAddEntryMutation } from "../../services/cookbook.service";
import { canEditCookbook, formatDate } from "../../common/cookbook";

const Cookbook = () => {
  const { user: currentUser } = useSelector((state) => state.auth);

  if (!currentUser) {
    return (
      <Box m="1.5rem 2.5rem">
        <Header title="Cookbook" />
        <Typography sx={{ mt: "1.5rem" }}>Log in to view the cookbook.</Typography>
      </Box>
    );
  }
  return <CookbookBoard canEdit={canEditCookbook(currentUser)} />;
};

// Entries grouped by category, alphabetically, with uncategorised ones last.
const groupByCategory = (entries) => {
  const groups = {};
  entries.forEach((entry) => {
    const key = entry.category || "";
    groups[key] = groups[key] || [];
    groups[key].push(entry);
  });
  return Object.keys(groups)
    .sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
    .map((key) => ({ name: key || "Uncategorized", entries: groups[key] }));
};

const CookbookBoard = ({ canEdit }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: entries, isLoading, isError } = useGetEntriesQuery("");
  const { data: matches, isFetching: searching } = useGetEntriesQuery(query, { skip: !query });
  const [addEntry, { isLoading: adding }] = useAddEntryMutation();

  const [promptOpen, setPromptOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);

  const categories = [...new Set((entries || []).map((entry) => entry.category).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
  const shown = query ? matches || [] : entries || [];

  const handleAdd = (values) =>
    addEntry(values)
      .unwrap()
      .then((entry) => navigate(`/cookbook/${entry._id}`));

  const paperSx = { p: "1.5rem", mt: "1.5rem", backgroundColor: theme.palette.background.alt };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Cookbook" subtitle="Topic notes to stay prepped for the wards, on call and exams" />

      <Box display="flex" flexWrap="wrap" alignItems="center" gap="0.75rem" mt="1.5rem">
        <TextField
          size="small"
          placeholder="Search topics and notes"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 260px", maxWidth: 480 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searching ? <CircularProgress size={16} /> : null,
          }}
        />
        <Box flexGrow={1} />
        <Button variant="outlined" color="secondary" startIcon={<AutoAwesomeOutlined />} onClick={() => setPromptOpen(true)}>
          AI prompt
        </Button>
        {canEdit && (
          <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => setEntryOpen(true)}>
            Add topic
          </Button>
        )}
      </Box>

      {isLoading && <CircularProgress sx={{ mt: "2rem" }} />}
      {isError && (
        <Typography color="error" sx={{ mt: "1.5rem" }}>
          Could not load the cookbook.
        </Typography>
      )}

      {!isLoading && !isError && (entries || []).length === 0 && (
        <Paper sx={paperSx}>
          <Typography variant="h5" fontWeight={700} mb="0.5rem">
            The cookbook is empty
          </Typography>
          {canEdit ? (
            <Typography color="text.secondary" component="div">
              <ol style={{ margin: 0, paddingLeft: "1.25rem", lineHeight: 1.8 }}>
                <li>Click <b>AI prompt</b>, type a topic (e.g. Diverticulitis) and copy the prompt.</li>
                <li>Paste it into Claude, ChatGPT or any AI chat.</li>
                <li>Click <b>Add topic</b> and paste (or upload) the Markdown it gives you.</li>
              </ol>
            </Typography>
          ) : (
            <Typography color="text.secondary">No topics have been added yet.</Typography>
          )}
        </Paper>
      )}

      {query && !searching && shown.length === 0 && (entries || []).length > 0 && (
        <Typography color="text.secondary" sx={{ mt: "1.5rem" }}>
          No topics match “{query}”.
        </Typography>
      )}

      {groupByCategory(shown).map((group) => (
        <Box key={group.name} mt="2rem">
          <Typography
            fontSize="0.8rem"
            fontWeight={700}
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="text.secondary"
            mb="0.75rem"
          >
            {group.name} · {group.entries.length}
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(230px, 1fr))" gap="0.75rem">
            {group.entries.map((entry) => (
              <Card key={entry._id} sx={{ backgroundColor: theme.palette.background.alt }}>
                <CardActionArea
                  onClick={() => navigate(`/cookbook/${entry._id}`)}
                  sx={{ p: "1rem 1.1rem", height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                >
                  <Typography fontWeight={700} fontSize="1rem">
                    {entry.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt="0.35rem">
                    Updated {formatDate(entry.updatedAt)}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>
      ))}

      <PromptDialog
        open={promptOpen}
        canEdit={canEdit}
        onClose={() => setPromptOpen(false)}
        onAddTopic={() => {
          setPromptOpen(false);
          setEntryOpen(true);
        }}
      />
      <EntryDialog
        open={entryOpen}
        entry={null}
        categories={categories}
        onClose={() => setEntryOpen(false)}
        onSave={handleAdd}
        saving={adding}
      />
    </Box>
  );
};

export default Cookbook;
